import * as z from "zod";

import { UNIT_KINDS, type GameEvent, type GameState, type UnitKind } from "../engine/types";

export const GameAchievementProgressSchema = z.object({
  trackingValid: z.boolean(),
  builtUnitKinds: z.array(z.enum(UNIT_KINDS)),
  unitsLost: z.number().int().nonnegative(),
  unitsKilled: z.number().int().nonnegative().default(0),
  hasCaptured: z.boolean().default(false),
  hasRepaired: z.boolean().default(false),
  startedWithAirport: z.boolean().default(false),
  escortTruckDamaged: z.boolean().default(false),
  cannonsDestroyed: z.number().int().nonnegative().default(0),
});

export type GameAchievementProgress = z.infer<typeof GameAchievementProgressSchema>;

function playerStartsWithAirport(state: Pick<GameState, "tiles">): boolean {
  return state.tiles.some((tile) => tile.kind === "airport" && tile.owner === "p1");
}

export function createGameAchievementProgress(
  state?: Pick<GameState, "tiles">,
): GameAchievementProgress {
  return {
    trackingValid: true,
    builtUnitKinds: [],
    unitsLost: 0,
    unitsKilled: 0,
    hasCaptured: false,
    hasRepaired: false,
    startedWithAirport: state ? playerStartsWithAirport(state) : false,
    escortTruckDamaged: false,
    cannonsDestroyed: 0,
  };
}

export function createUntrackedGameAchievementProgress(): GameAchievementProgress {
  return {
    trackingValid: false,
    builtUnitKinds: [],
    unitsLost: 0,
    unitsKilled: 0,
    hasCaptured: false,
    hasRepaired: false,
    startedWithAirport: false,
    escortTruckDamaged: false,
    cannonsDestroyed: 0,
  };
}

export function resolveGameAchievementProgress({
  stored,
  state,
}: {
  stored: unknown;
  state: Pick<GameState, "tiles" | "currentDay" | "status">;
}): GameAchievementProgress {
  if (stored !== undefined && stored !== null) {
    return GameAchievementProgressSchema.parse(stored);
  }
  if (state.status === "in_progress" && state.currentDay === 1) {
    return createGameAchievementProgress(state);
  }
  return createUntrackedGameAchievementProgress();
}

function playerLostUnit(event: GameEvent): boolean {
  if (event.type === "cannonFired") {
    return event.playerId === "p2" && event.killed;
  }
  if (event.type === "attacked") {
    return event.playerId === "p2" && event.killed && event.targetKind !== "cannon";
  }
  return event.type === "countered" && event.playerId === "p2" && event.killed;
}

function playerKilledUnit(event: GameEvent): boolean {
  if (event.type === "attacked") {
    return event.playerId === "p1" && event.killed && event.targetKind !== "cannon";
  }
  return event.type === "countered" && event.playerId === "p1" && event.killed;
}

function playerSupplyTruckDamaged(event: GameEvent): boolean {
  if (event.type === "attacked") {
    return event.playerId === "p2" && event.targetKind === "supplyTruck" && event.damage > 0;
  }
  if (event.type === "cannonFired") {
    return event.playerId === "p2" && event.targetKind === "supplyTruck" && event.damage > 0;
  }
  return false;
}

function playerDestroyedCannon(event: GameEvent): boolean {
  return (
    event.type === "attacked" &&
    event.playerId === "p1" &&
    event.killed &&
    event.targetKind === "cannon"
  );
}

type ProgressFoldState = {
  builtKinds: Set<UnitKind>;
  unitsLost: number;
  unitsKilled: number;
  hasCaptured: boolean;
  hasRepaired: boolean;
  escortTruckDamaged: boolean;
  cannonsDestroyed: number;
};

function applyEventToProgressFold(state: ProgressFoldState, event: GameEvent): void {
  if (event.type === "unitBuilt" && event.playerId === "p1") {
    state.builtKinds.add(event.kind);
  }
  if (playerLostUnit(event)) {
    state.unitsLost += 1;
  }
  if (playerKilledUnit(event)) {
    state.unitsKilled += 1;
  }
  if (event.type === "captured" && event.playerId === "p1") {
    state.hasCaptured = true;
  }
  if (event.type === "unitRepaired" && event.playerId === "p1") {
    state.hasRepaired = true;
  }
  if (playerSupplyTruckDamaged(event)) {
    state.escortTruckDamaged = true;
  }
  if (playerDestroyedCannon(event)) {
    state.cannonsDestroyed += 1;
  }
}

export function foldGameAchievementProgress({
  progress,
  events,
}: {
  progress: GameAchievementProgress;
  events: readonly GameEvent[];
}): GameAchievementProgress {
  const foldState: ProgressFoldState = {
    builtKinds: new Set(progress.builtUnitKinds),
    unitsLost: progress.unitsLost,
    unitsKilled: progress.unitsKilled,
    hasCaptured: progress.hasCaptured,
    hasRepaired: progress.hasRepaired,
    escortTruckDamaged: progress.escortTruckDamaged,
    cannonsDestroyed: progress.cannonsDestroyed,
  };

  for (const event of events) {
    applyEventToProgressFold(foldState, event);
  }

  return {
    ...progress,
    builtUnitKinds: [...foldState.builtKinds],
    unitsLost: foldState.unitsLost,
    unitsKilled: foldState.unitsKilled,
    hasCaptured: foldState.hasCaptured,
    hasRepaired: foldState.hasRepaired,
    escortTruckDamaged: foldState.escortTruckDamaged,
    cannonsDestroyed: foldState.cannonsDestroyed,
  };
}
